import { isLocationMessage, parseLocationMessage } from './locationUtils';

export const MIN_SEARCH_QUERY_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

export const getMessageSearchableText = (message = {}) => {
  if (!message || message.msgType === 'date') {
    return '';
  }

  const parts = [];

  if (message.text) {
    parts.push(String(message.text));
  }
  if (message.caption) {
    parts.push(String(message.caption));
  }
  if (message.file_name) {
    parts.push(String(message.file_name));
  }
  if (message.preset_name) {
    parts.push(String(message.preset_name));
  }
  if (message.location_name) {
    parts.push(String(message.location_name));
  }
  if (message.location_address) {
    parts.push(String(message.location_address));
  }

  if (isLocationMessage(message)) {
    const location = parseLocationMessage(message);
    if (location?.label) {
      parts.push(location.label);
    }
  }

  return parts.join(' ').trim();
};

export const messageMatchesQuery = (message, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (normalizedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    return false;
  }

  const haystack = getMessageSearchableText(message).toLowerCase();
  return haystack.includes(normalizedQuery);
};

export const findLocalSearchMatches = (messages = [], query = '') => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (normalizedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    return [];
  }

  return messages
    .filter((message) => message?.msgType !== 'date' && message?.id)
    .filter((message) => messageMatchesQuery(message, normalizedQuery))
    .map((message) => ({
      id: message.id,
      source: message.from === 'me' ? 'chat' : 'report',
      from: message.from,
      created_at: message.created_at,
      snippet: getMessageSearchableText(message).slice(0, 120),
      origin: 'local',
    }))
    .sort((left, right) => new Date(left.created_at) - new Date(right.created_at));
};

export const mergeSearchMatches = (localMatches = [], remoteMatches = []) => {
  const seen = new Set();
  const merged = [];

  [...localMatches, ...remoteMatches].forEach((match) => {
    const key = `${match.source}:${match.id}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(match);
  });

  return merged.sort(
    (left, right) => new Date(left.created_at) - new Date(right.created_at)
  );
};

export const buildMatchKey = (match) => `${match?.source || 'report'}:${match?.id}`;

export const highlightSearchText = (text, query) => {
  const source = String(text || '');
  const normalizedQuery = String(query || '').trim();
  if (!source || normalizedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    return source;
  }

  const lowerSource = source.toLowerCase();
  const lowerQuery = normalizedQuery.toLowerCase();
  const index = lowerSource.indexOf(lowerQuery);
  if (index === -1) {
    return source;
  }

  const before = source.slice(0, index);
  const match = source.slice(index, index + normalizedQuery.length);
  const after = source.slice(index + normalizedQuery.length);

  return (
    <>
      {before}
      <mark className="chat-search-highlight">{match}</mark>
      {after}
    </>
  );
};
